/**
 * Booking pages (docs/features/booking-pages.md; Google Calendar "appointment schedules"): a person's page of times
 * others can book with them, at bundle.direct/book/<code>, without an account. Availability = the page's weekly hours
 * (and date-specific hours) minus the owner's busy events, within the scheduling window. A booking is an ordinary
 * event on the owner's calendar with the guest as an outside attendee; the guest gets an .ics invite, reminders and
 * links to reschedule or cancel (the manage token).
 */
import { z } from "zod";
import { EmailAddress, Id, Timestamp } from "./entities.ts";

/** A wall-clock time "HH:mm" (24 h) in the page's time zone. */
export const ClockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$|^24:00$/);
export type ClockTime = z.infer<typeof ClockTime>;

/** One open period of a day; `end` after `start` ("24:00" = midnight at the end of the day). */
export const TimeRange = z.object({ start: ClockTime, end: ClockTime });
export type TimeRange = z.infer<typeof TimeRange>;

/** Open periods per weekday, 0 = Sunday … 6 = Saturday; an empty day is unavailable. */
export const WeeklyHours = z.array(z.array(TimeRange).max(12)).length(7);
export type WeeklyHours = z.infer<typeof WeeklyHours>;

/** Different hours on one date ("YYYY-MM-DD" in the page's zone); no ranges = unavailable that day. */
export const DateHours = z.object({ date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), ranges: z.array(TimeRange).max(12) });
export type DateHours = z.infer<typeof DateHours>;

/** Where the appointment happens. `bundle_meeting` makes a meeting link (/m/<code>) for each booking. */
export const BookingLocation = z.object({
  kind: z.enum(["bundle_meeting", "in_person", "phone", "none"]),
  /** The address (in person) or the number to call (phone); absent for phone = the guest gives their number. */
  detail: z.string().max(500).optional(),
});
export type BookingLocation = z.infer<typeof BookingLocation>;

/** An extra question on the booking form (first name, last name and email are always asked). */
export const BookingQuestion = z.object({ id: z.string().min(1).max(40), label: z.string().min(1).max(200), required: z.boolean() });
export type BookingQuestion = z.infer<typeof BookingQuestion>;

/** The settings people edit (create and update take these). */
export const BookingPageSettings = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(5000).optional(),
  /** Appointment length; slots start this far apart. */
  durationMinutes: z.number().int().min(5).max(480),
  /** IANA zone the hours are in. */
  timeZone: z.string().min(1).max(64),
  weekly: WeeklyHours,
  dateHours: z.array(DateHours).max(200).default([]),
  /** How far ahead people can book (days from today); null = no limit. Google's default: 60 (core defaultBookingPage). */
  maxDaysAhead: z.number().int().min(1).max(730).nullable(),
  /** Shortest notice (minutes before the start). Google's default: 4 hours. */
  minNoticeMinutes: z.number().int().min(0).max(43_200).default(240),
  /** Kept free around busy events and other bookings. */
  bufferMinutes: z.number().int().min(0).max(240).default(0),
  /** Bookings from this page per day (the page's zone); null or absent = no limit. */
  maxPerDay: z.number().int().min(1).max(100).nullable().optional(),
  /** Guests may add people to the booked event. */
  guestsCanInvite: z.boolean().default(false),
  /** Busy events on my calendars block times (Google: "Check calendars for availability"). */
  checkCalendars: z.boolean().default(true),
  /** Google's default: a meeting link (core defaultBookingPage). */
  location: BookingLocation,
  questions: z.array(BookingQuestion).max(10).default([]),
  /** Guests confirm their email with a code before booking. */
  verifyEmail: z.boolean().default(false),
  /** Email reminders to the guest, minutes before the start (up to 5, like Google). */
  reminders: z.array(z.number().int().min(5).max(40_320)).max(5).default([1440, 60]),
  /** Colour token name (`tag.<color>`). */
  color: z.string().max(20).optional(),
});
export type BookingPageSettings = z.infer<typeof BookingPageSettings>;

export const BookingPage = BookingPageSettings.extend({
  id: Id,
  ownerId: Id,
  /** The link code: bundle.direct/book/<code>. */
  code: z.string().min(1).max(40),
  url: z.string().max(500),
  /** Paused pages show "not taking bookings"; the link keeps working. */
  paused: z.boolean(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type BookingPage = z.infer<typeof BookingPage>;

/** GET /v1/w/:ws/calendar/booking-pages — mine. */
export const ListBookingPagesResponse = z.object({ pages: z.array(BookingPage) });
export type ListBookingPagesResponse = z.infer<typeof ListBookingPagesResponse>;

/** POST /v1/w/:ws/calendar/booking-pages */
export const CreateBookingPageRequest = BookingPageSettings;
export type CreateBookingPageRequest = z.infer<typeof CreateBookingPageRequest>;

/** PATCH /v1/w/:ws/calendar/booking-pages/:pageId — any settings, and `paused`. */
export const UpdateBookingPageRequest = BookingPageSettings.partial().extend({
  description: z.string().max(5000).nullable().optional(),
  color: z.string().max(20).nullable().optional(),
  paused: z.boolean().optional(),
});
export type UpdateBookingPageRequest = z.infer<typeof UpdateBookingPageRequest>;

/** Create, get, update and reset-link answer with the page. */
export const BookingPageResponse = z.object({ page: BookingPage });
export type BookingPageResponse = z.infer<typeof BookingPageResponse>;

// ----------------------------------------------------------------------------------------------------------------
// The public side (no workspace auth; rate-limited per IP; Turnstile on writes)

/** GET /v1/public/book/:code — what the booking page shows. */
export const PublicBookingPage = z.object({
  code: z.string(),
  title: z.string(),
  description: z.string().optional(),
  durationMinutes: z.number().int(),
  owner: z.object({ name: z.string(), avatarUrl: z.string().optional() }),
  workspaceName: z.string(),
  location: BookingLocation,
  questions: z.array(BookingQuestion),
  verifyEmail: z.boolean(),
  /** The owner's zone (guests see times in theirs). */
  timeZone: z.string(),
  paused: z.boolean(),
});
export type PublicBookingPage = z.infer<typeof PublicBookingPage>;

/** GET /v1/public/book/:code/slots?from&to — open start times in [from, to) (at most 42 days). */
export const BookingSlotsQuery = z.object({ from: z.coerce.number().int().nonnegative(), to: z.coerce.number().int().nonnegative() });
export type BookingSlotsQuery = z.infer<typeof BookingSlotsQuery>;

export const BookingSlotsResponse = z.object({
  slots: z.array(Timestamp),
  /** The first open start after `to` (for "Jump to the next open date"); absent = none within the window. */
  next: Timestamp.optional(),
});
export type BookingSlotsResponse = z.infer<typeof BookingSlotsResponse>;

/** POST /v1/public/book/:code/verify — email a 6-digit code (pages with `verifyEmail`). */
export const BookingVerifyRequest = z.object({ email: EmailAddress, turnstileToken: z.string().max(4096).optional() });
export type BookingVerifyRequest = z.infer<typeof BookingVerifyRequest>;

export const BookingAnswer = z.object({ questionId: z.string().max(40), value: z.string().max(2000) });
export type BookingAnswer = z.infer<typeof BookingAnswer>;

/** POST /v1/public/book/:code — book `start`. */
export const BookRequest = z.object({
  start: Timestamp,
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().min(1).max(100),
  email: EmailAddress,
  answers: z.array(BookingAnswer).max(10).default([]),
  /** The guest's zone (emails show times in it). */
  timeZone: z.string().min(1).max(64),
  turnstileToken: z.string().max(4096).optional(),
  /** The emailed code, on pages with `verifyEmail`. */
  verificationCode: z.string().max(12).optional(),
});
export type BookRequest = z.infer<typeof BookRequest>;

/** A booking as its guest sees it (the manage page). */
export const PublicBooking = z.object({
  /** The page, to book again. */
  pageCode: z.string(),
  title: z.string(),
  ownerName: z.string(),
  start: Timestamp,
  end: Timestamp,
  timeZone: z.string(),
  location: BookingLocation,
  /** The meeting link (Bundle meeting). */
  meetingUrl: z.string().optional(),
  guestName: z.string(),
  guestEmail: EmailAddress,
  status: z.enum(["confirmed", "cancelled"]),
  cancelledBy: z.enum(["guest", "owner"]).optional(),
  cancelReason: z.string().optional(),
});
export type PublicBooking = z.infer<typeof PublicBooking>;

/** The answer to booking or rescheduling: the booking and its manage token (also in the email links). */
export const BookResponse = z.object({ booking: PublicBooking, token: z.string() });
export type BookResponse = z.infer<typeof BookResponse>;

/** POST /v1/public/bookings/:token/reschedule */
export const RescheduleBookingRequest = z.object({ start: Timestamp, turnstileToken: z.string().max(4096).optional() });
export type RescheduleBookingRequest = z.infer<typeof RescheduleBookingRequest>;

/** POST /v1/public/bookings/:token/cancel */
export const CancelBookingRequest = z.object({ reason: z.string().max(1000).optional() });
export type CancelBookingRequest = z.infer<typeof CancelBookingRequest>;
